import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, input, OnInit, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { BANKRUPTCY_RISK_SCORE_API_SERVICE_TOKEN, BankruptcyRiskScoreApiService } from '@nk/api-lib';
import { CompanyLifeState, CompanyRiskScoreAnalysis } from '@nk/data-entities-lib';
import { CardComponent, CardContentComponent, CardHeaderComponent, PercentagePipe } from '@nk/ui-lib';
import {
  arrayMin,
  DateFormats,
  Dict,
  dictToValues,
  LOADING_STATE_HANDLER_SERVICE_TOKEN,
  LoadingStateHandlerService,
} from '@nk/utils-lib';
import { Observable } from 'rxjs';
import { CompanyLifeStateService } from '../../services/company-life-state.service';
import { BANKRUPTCY_RISK_SCORE_COLOR } from '../../utils/bankruptsy-risk-score.utils';
import { BankruptcyRiskScoreHelpInfoComponent } from '../bankruptcy-risk-score-help-info/bankruptcy-risk-score-help-info.component';
import { CompanyAnalysisBankruptcyRiskChartComponent } from '../company-analysis-bankruptcy-risk-chart/company-analysis-bankruptcy-risk-chart.component';
import { CompanyAnalysisCompanyLifeStateCardComponent } from '../company-analysis-company-life-state-card/company-analysis-company-life-state-card.component';
import { CompanyAnalysisRiskLayoutComponent } from '../company-analysis-risk-layout/company-analysis-risk-layout.component';
import { CompanyAnalysisRiskLoadingComponent } from '../company-analysis-risk-loading/company-analysis-risk-loading.component';
import { RiskLevelIconComponent } from '../risk-level-icon/risk-level-icon.component';

@Component({
  selector: 'nk-company-analysis-bankruptcy-risk-card',
  imports: [
    AsyncPipe,
    DatePipe,
    TranslateModule,
    CardComponent,
    CardHeaderComponent,
    CardContentComponent,
    RiskLevelIconComponent,
    PercentagePipe,
    BankruptcyRiskScoreHelpInfoComponent,
    CompanyAnalysisRiskLoadingComponent,
    CompanyAnalysisBankruptcyRiskChartComponent,
    CompanyAnalysisRiskLayoutComponent,
    CompanyAnalysisCompanyLifeStateCardComponent,
  ],
  templateUrl: 'company-analysis-bankruptcy-risk-card.component.html',
})
export class CompanyAnalysisBankruptcyRiskComponent implements OnInit {
  public companyUuid = input.required<string>();
  public printVersion = input(false);

  public loading = true;
  public riskScoreAnalysis: CompanyRiskScoreAnalysis | null = null;
  public prioritizedCompanyRiskScoreAnalysisList: CompanyRiskScoreAnalysis[] = [];
  public noCompanyRiskScoreAvailable = false;
  public companyLifeState$!: Observable<CompanyLifeState>;
  public companyBankruptsyRiskScoreColor: Dict<string> = BANKRUPTCY_RISK_SCORE_COLOR;
  public dateFormats = DateFormats;

  private _destroy = inject(DestroyRef);

  constructor(
    @Inject(BANKRUPTCY_RISK_SCORE_API_SERVICE_TOKEN)
    private _bankruptcyRiskScoreService: BankruptcyRiskScoreApiService,
    private _companyLifeStateService: CompanyLifeStateService,
    private _cdr: ChangeDetectorRef,
    @Optional()
    @Inject(LOADING_STATE_HANDLER_SERVICE_TOKEN)
    private _reportLoadingStateService: LoadingStateHandlerService,
  ) {}

  public ngOnInit(): void {
    this.companyLifeState$ = this._companyLifeStateService.getCompanyLifeState(this.companyUuid());
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this.constructor.name, true);
    }

    this._bankruptcyRiskScoreService.companyRiskScoreAnalysisCollectionByModelCache
      .get(this.companyUuid())
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((companyRiskScore) => {
        if (companyRiskScore.models.length > 0) {
          const latestAnalysis = dictToValues(companyRiskScore.latestAnalyses);
          this.riskScoreAnalysis = arrayMin(latestAnalysis, (v) => v.priority.model_rank);

          const modelUuid = this.riskScoreAnalysis?.model.uuid;
          if (typeof modelUuid === 'string') {
            this.prioritizedCompanyRiskScoreAnalysisList = companyRiskScore.allAnalyses[modelUuid];
          }
        } else {
          this.noCompanyRiskScoreAvailable = true;
        }
        this.loading = false;
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this.constructor.name, false);
        }
        this._cdr.markForCheck();
      });
  }
}
